<!--
    The input group for a variable belonging to a SLIDER question on a QuestionForm.
-->
<template>
  <b-container class="question-form-slider">
    <b-row class="text-center">
      <b-col :cols="12" class="text-center">
        <div
          class="question-form-slider__slider"
          :disabled="disabled"
          :ref="`slider_${uid}`"
          :key="uid"
        ></div>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col cols="4">{{ minLabel }}</b-col>
      <b-col cols="4" class="question-form-slider__value phairify-heading-h2">
        <span class="question-form-slider__value-prefix">
          {{ variable.prefix }}
        </span>
        <span class="question-form-slider__value-value">
          {{ formattedValue }}
        </span>
        <span class="question-form-slider__value-suffix">
          {{ value === 1 ? variable.singular_suffix : variable.suffix }}
        </span>
      </b-col>
      <b-col cols="4" class="text-right">{{ maxLabel }}</b-col>
    </b-row>
    <div class="question-form-slider__value" v-if="value !== null"></div>
  </b-container>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import noUiSlider from 'nouislider';

export default {
  name: 'questionFormInputSlider',
  props: {
    uid: String, // unique ID to use in the DOM ID of the slider element
    value: {
      validator: (prop) => typeof prop === 'number' || prop === null,
    }, // initial value
    disabled: Boolean,
    onUpdate: Function, // callback to store the updated value when the slider is moved
    onSet: Function, // callback to store the updated value when the slider is set
    preview: Boolean,
    variable: Object,
  },
  data() {
    return {
      sliderElt: null,
      slider: null,
      sliderValue: 0,
    };
  },
  computed: {
    step() {
      return this.variable.step ? this.variable.step : 1;
    },
    min() {
      return this.variable.min ? this.variable.min : 0;
    },
    max() {
      return this.variable.max ? this.variable.max : 1;
    },
    minLabel: function () {
      return this.variable.min_label ? this.variable.min_label : '';
    },
    maxLabel: function () {
      return this.variable.max_label ? this.variable.max_label : '';
    },
    formattedValue() {
      if (this.value === null) return '0';
      return Number(this.sliderValue).toLocaleString('en-US');
    },
  },
  methods: {
    // Initialize the no-ui slider - this must be called both in mount() and in the uid
    // watcher (and the "key" attribute must be used on the slider elt), to prevent Vue
    // from reusing slider components between adjacent questions.
    // Otherwise the value from one question would bleed into the other - the slider
    // would initially display the value for the current question, but incrementing/
    // decrementing it would update it relative to the value of the previously displayed question.

    initSlider() {
      this.$nextTick(function () {
        let sliderRef = this.$refs[`slider_${this.uid}`];
        // initialize slider
        if (sliderRef) {
          noUiSlider.create(sliderRef, {
            start: this.value ? this.value : 0,
            connect: [true, false],
            step: parseInt(this.step),
            range: {
              min: [parseInt(this.min)],
              max: [parseInt(this.max)],
            },
          });
        }
        // handle slider updates
        sliderRef.noUiSlider.on('set', (values) => {
          let val = parseInt(values[0]);
          if (!this.disabled) {
            this.sliderValue = val;
            if (this.onSet) {
              this.onSet(this.uid, val);
            }
          }
        });
        sliderRef.noUiSlider.on('update', (values) => {
          let val = parseInt(values[0]);
          if (!this.disabled) {
            this.sliderValue = val;
            if (this.onUpdate) {
              this.onUpdate(this.uid, val);
            }
          }
        });
      });
    },
    getCurrentSliderValue: function () {
      return this.sliderValue;
    },
  },
  mounted() {
    this.initSlider();
  },
  watch: {
    // handle Decline to answer
    value(val) {
      if (val === null && this.$refs[`slider_${this.uid}`]) {
        this.$refs[`slider_${this.uid}`].noUiSlider.setHandle(0, 0, false);
      } else if (!val && this.$refs[`slider_${this.uid}`]) {
        this.sliderValue = 0;
        this.$refs[`slider_${this.uid}`].noUiSlider.set(0);
      } else if (this.$refs[`slider_${this.uid}`]) {
        const values = this.$refs[`slider_${this.uid}`].noUiSlider.get();
        const curVal = parseInt(Array.isArray(values) ? values[0] : values);
        if (curVal !== val) {
          this.$refs[`slider_${this.uid}`].noUiSlider.set(val);
        }
      }
    },
    disabled: function (newVal, currVal) {
      if (currVal && !newVal) {
        const values = this.$refs[`slider_${this.uid}`].noUiSlider.get();
        const currSliderVal = parseInt(
          Array.isArray(values) ? values[0] : values
        );
        this.$refs[`slider_${this.uid}`].noUiSlider.set(currSliderVal);
      }
    },
    // prevent reuse of slider widget
    uid() {
      this.initSlider();
    },
    // re-init slider after changing variable
    min: function (newMin, oldMin) {
      this.$nextTick(() => {
        if (oldMin !== newMin) {
          this.$refs[`slider_${this.uid}`].noUiSlider.updateOptions({
            start: this.value < newMin ? newMin : this.value,
            range: {
              min: [parseInt(newMin)],
              max: [parseInt(this.max)],
            },
          });
        }
      });
    },
    max: function (newMax, oldMax) {
      this.$nextTick(() => {
        if (oldMax !== newMax) {
          this.$refs[`slider_${this.uid}`].noUiSlider.updateOptions({
            start: this.value > newMax ? newMax : this.value,
            range: {
              min: [parseInt(this.min)],
              max: [parseInt(newMax)],
            },
          });
        }
      });
    },
    step: function (newStep, oldStep) {
      this.$nextTick(() => {
        if (oldStep !== newStep) {
          this.$refs[`slider_${this.uid}`].noUiSlider.updateOptions({
            step: parseInt(newStep),
          });
        }
      });
    },
  },
};
</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
.question-form-slider {
  margin: 5px 0;
  min-height: 60px;
}

.question-form-slider__slider {
  margin: 5px 30px 0 30px;
}

.question-form-slider__decrement {
  font-weight: bold;
  font-size: 1.5em;
  height: 29px;
  line-height: 26px;
  padding: 0 8px 2px 8px;
}

.question-form-slider__increment {
  font-weight: bold;
  font-size: 1.5em;
  height: 29px;
  line-height: 26px;
  padding: 0 8px 2px 8px;
}

.question-form-slider__value {
  text-align: center;
}

.question-form-slider__value-value {
  font-weight: bold;
}

// noUiSlider overrides
.question-form-slider {
  .noUi-pips-horizontal {
    color: #ccc;
  }

  .noUi-marker-horizontal.noUi-marker-large {
    height: 10px;
    background-color: #ccc;
  }

  .noUi-value {
    line-height: 1.4em;
  }
}
</style>
